import { Clock, RefreshCw, Shield, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useGuardContext } from '@/hooks/useGuardContext';
import { getGates, getPmUsers, startGuardShift, changeGuardShift } from '@/services/api.client';
import { useTranslation } from '@/hooks/useTranslation';
import type { Gate } from '@/types';

/**
 * ShiftStartPage — Prompts guard to start shift before accessing gate operations.
 * Supports both starting a new shift and shift handover (Mode B shared devices).
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5
 */
export function ShiftStartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { propertyId, setActiveShift, activeShift } = useGuardContext();

  // Mode B (shared device) guards should never land here — redirect them to the
  // roster-based shift selection page which uses DeviceOrJwtAuthGuard and doesn't
  // require an existing shift to call POST /guard/shifts/select.
  const isDeviceSession = !!localStorage.getItem('sg_device_session');

  const [selectedGateId, setSelectedGateId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [isHandover, setIsHandover] = useState(false);
  const [incomingGuardId, setIncomingGuardId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Fetch available gates for the property
  const { data: gates = [], isLoading: gatesLoading } = useQuery<Gate[]>({
    queryKey: ['gates', propertyId],
    queryFn: () => getGates(propertyId),
    enabled: !!propertyId,
  });

  // Fetch guards for handover dropdown — only loaded when handover panel is open
  const { data: guardsData } = useQuery({
    queryKey: ['pm-users', propertyId, 'guard'],
    queryFn: () => getPmUsers(propertyId, { role: 'guard', perPage: 100 }),
    enabled: !!propertyId && isHandover,
  });
  const guards = guardsData?.data ?? [];

  // Start shift mutation
  const startMutation = useMutation({
    mutationFn: startGuardShift,
    onSuccess: (shiftInfo) => {
      setActiveShift(shiftInfo);
      void navigate('/guard');
    },
    onError: (err: { message?: string }) => {
      setError(err.message ?? t('common.error'));
    },
  });

  // Change shift (handover) mutation
  const changeMutation = useMutation({
    mutationFn: changeGuardShift,
    onSuccess: (shiftInfo) => {
      setActiveShift(shiftInfo);
      void navigate('/guard');
    },
    onError: (err: { message?: string }) => {
      setError(err.message ?? t('common.error'));
    },
  });

  const isSubmitting = startMutation.isPending || changeMutation.isPending;

  // Mode B redirect must come after all hooks to satisfy rules-of-hooks
  if (isDeviceSession && !activeShift) {
    return <Navigate to="/guard/select" replace />;
  }

  const handleStartShift = () => {
    setError(null);
    if (!selectedGateId) {
      setError(t('guard.shift.gateRequired'));
      return;
    }
    let deviceId = localStorage.getItem('sg_guard_device_id');
    if (!deviceId) {
      deviceId = `web-${window.crypto.randomUUID?.() ?? Date.now()}`;
      localStorage.setItem('sg_guard_device_id', deviceId);
    }
    startMutation.mutate({
      gateId: selectedGateId,
      deviceId,
      ...(deviceName ? { deviceName } : {}),
    });
  };

  const handleHandover = () => {
    setError(null);
    if (!incomingGuardId.trim()) {
      setError(t('guard.shift.guardIdRequired'));
      return;
    }
    changeMutation.mutate({ incomingGuardId: incomingGuardId.trim() });
  };

  // Handover view for Mode B shared devices
  if (activeShift && isHandover) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="w-full max-w-md space-y-6 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-[var(--status-blue-tint)]">
              <UserCheck className="size-6 text-[var(--status-blue-text)]" />
            </div>
            <h1 className="text-xl text-[var(--text-primary)]">{t('guard.shift.handoverTitle')}</h1>
            <p className="text-sm text-[var(--text-secondary)]">
              {t('guard.shift.handoverDescription')}
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="incoming-guard-select">{t('guard.shift.incomingGuardId')}</Label>
              <Select value={incomingGuardId} onValueChange={setIncomingGuardId}>
                <SelectTrigger id="incoming-guard-select" className="w-full min-h-[44px]">
                  <SelectValue placeholder={t('guard.shift.incomingGuardIdPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {guards.length === 0 ? (
                    <SelectItem value="_none" disabled>
                      {t('guard.shift.noGuardsAvailable')}
                    </SelectItem>
                  ) : (
                    guards.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.fullName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {error && (
              <p id="handover-error" className="text-sm text-[var(--text-error)]" role="alert">
                {error}
              </p>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 min-h-[44px]"
                onClick={() => setIsHandover(false)}
                disabled={isSubmitting}
              >
                {t('common.back')}
              </Button>
              <Button
                className="flex-1 min-h-[44px]"
                onClick={handleHandover}
                disabled={isSubmitting || !incomingGuardId.trim()}
              >
                {isSubmitting && <RefreshCw className="mr-2 size-4 animate-spin" />}
                {t('guard.shift.confirmHandover')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default: start shift view
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-md space-y-6 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
            <Shield className="size-6 text-primary" />
          </div>
          <h1 className="text-xl text-[var(--text-primary)]">{t('guard.shift.startTitle')}</h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {t('guard.shift.startDescription')}
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="gate-select">{t('guard.shift.selectGate')}</Label>
            <Select value={selectedGateId} onValueChange={setSelectedGateId}>
              <SelectTrigger id="gate-select" className="w-full min-h-[44px]">
                <SelectValue placeholder={t('guard.shift.selectGatePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {gatesLoading ? (
                  <SelectItem value="_loading" disabled>
                    {t('common.loading')}
                  </SelectItem>
                ) : gates.length === 0 ? (
                  <SelectItem value="_empty" disabled>
                    {t('guard.shift.noGates')}
                  </SelectItem>
                ) : (
                  gates.map((gate) => (
                    <SelectItem key={gate.id} value={gate.id}>
                      {gate.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="device-name">
              {t('guard.shift.deviceName')}{' '}
              <span className="text-[var(--text-tertiary)]">({t('common.optional')})</span>
            </Label>
            <Input
              id="device-name"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder={t('guard.shift.deviceNamePlaceholder')}
            />
          </div>

          {error && (
            <p id="shift-error" className="text-sm text-[var(--text-error)]" role="alert">
              {error}
            </p>
          )}

          <Button
            className="w-full min-h-[44px]"
            onClick={handleStartShift}
            disabled={isSubmitting || !selectedGateId}
          >
            {isSubmitting && <RefreshCw className="mr-2 size-4 animate-spin" />}
            <Clock className="mr-2 size-4" />
            {t('guard.shift.startButton')}
          </Button>

          {activeShift && (
            <Button
              variant="outline"
              className="w-full min-h-[44px]"
              onClick={() => setIsHandover(true)}
              disabled={isSubmitting}
            >
              <UserCheck className="mr-2 size-4" />
              {t('guard.shift.handoverButton')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
